/**
 * @file
 * Popover placement, the body portal and the one-open closer
 * (berTags.popover).
 */

/* eslint-disable no-use-before-define -- the UI closures in this file are
   mutually recursive (render/place/applyView call each other), so some
   references necessarily precede their definitions. */
((Drupal, once) => {
  const { berTags } = Drupal;
  const { el } = berTags.dom;

  // stacking contexts, the one-open-at-a-time closer, and the open/close
  // skeleton every balloon repeats.
  const popover = (() => {
    let portalEl = null;
    let portalActive = null;
    // Keeps an open balloon pinned to a trigger that moves when tags or
    // tiles are added around it, so no consumer re-pins by hand. Portaled
    // and in-flow popovers share this one mechanism (see follow()). A rAF
    // poll, not a ResizeObserver, is used on purpose: the "+" moves because
    // a sibling grew, which shifts its position without changing the size of
    // any element an observer could watch.
    let followRaf = null;
    let followAdd = null;
    let followRect = null;
    // The active portal balloon's anchoring session (anchorAt).
    let portalSession = null;

    const place = (add, pop, opts = {}) => {
      pop.classList.remove('ber-tags__pop--up', 'ber-tags__pop--end');
      pop.style.marginLeft = '';
      if (opts.body) {
        opts.body.style.maxHeight = '';
      }

      const margin = 8;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;
      const trigger = add.getBoundingClientRect();
      const rect = pop.getBoundingClientRect();

      const potential =
        pop.dataset.berPotential !== undefined
          ? Number(pop.dataset.berPotential)
          : 340;
      const potentialHeight = Math.max(rect.height, potential);
      const gap = rect.top - trigger.bottom;
      const below = viewportHeight - rect.top - margin;
      const above = trigger.top - gap - margin;
      const up = potentialHeight > below && above > below;
      if (up) {
        pop.classList.add('ber-tags__pop--up');
      }
      if (opts.body) {
        const room = up ? above : below;
        const chrome = rect.height - opts.body.offsetHeight;
        opts.body.style.maxHeight = `${Math.max(96, Math.min(256, room - chrome))}px`;
      }

      if (opts.caret) {
        // Fixed-width popovers measure their real edges.
        if (rect.right > viewportWidth - margin) {
          pop.classList.add('ber-tags__pop--end');
        }
        const flipped = pop.getBoundingClientRect();
        if (flipped.left < margin) {
          pop.style.marginLeft = `${margin - flipped.left}px`;
        }
        const anchorRect = add.getBoundingClientRect();
        const final = pop.getBoundingClientRect();
        const caret = Math.min(
          Math.max(anchorRect.left + anchorRect.width / 2 - final.left, 14),
          final.width - 14,
        );
        pop.style.setProperty('--ber-caret-x', `${caret}px`);
      } else {
        // max-content popovers measure against their potential width.
        const rtl = getComputedStyle(pop).direction === 'rtl';
        // Matches the .ber-tags__pop max-width clamp (28rem).
        const potentialWidth = Math.min(448, viewportWidth - 32);
        const overflow = rtl
          ? rect.right - potentialWidth < margin
          : rect.left + potentialWidth > viewportWidth - margin;
        if (overflow) {
          pop.classList.add('ber-tags__pop--end');
        }
      }

      // Every reposition path ends here (refresh, follow, anchorAt's
      // onPlace), so this is where a move is announced. Anything anchored
      // to something inside the balloon listens for this instead of being
      // called by name from here - the tooltip is the first consumer.
      // It bubbles from the balloon, but a portaled one bubbles through the
      // body portal, NOT the widget: listen on document and filter on
      // detail.trigger. Fires once per placement pass and a reposition runs
      // several, so listeners stay cheap and idempotent.
      pop.dispatchEvent(
        new CustomEvent('ber-popover:place', {
          bubbles: true,
          detail: { trigger: add, pop },
        }),
      );
    };

    const portalFor = (add, pop, home, opts = {}) => {
      if (!portalEl) {
        portalEl = el('span', 'ber-tags__anchor ber-tags ber-upload__portal');
        document.body.appendChild(portalEl);
        // Layout shifts move the trigger; follow it while a balloon is open.
        window.addEventListener('resize', refresh);
        // The page-global outside-click closer only strips is-open; finish
        // the job here (trigger back to collapsed, balloon back home) so
        // hover-only controls hide again.
        new MutationObserver(() => {
          if (portalActive && !portalActive.pop.classList.contains('is-open')) {
            const ended = portalActive;
            portalActive = null;
            ended.add.setAttribute('aria-expanded', 'false');
            unportal(ended.pop, ended.home);
          }
        }).observe(portalEl, {
          attributes: true,
          subtree: true,
          attributeFilter: ['class'],
        });
      }
      [...portalEl.children].forEach((child) => {
        if (child !== pop) {
          child.remove();
        }
      });
      // Only move it in when it is not already there: re-appending an
      // existing child detaches and reattaches it, which resets the scroll
      // position of the popover's scrollable list.
      if (pop.parentNode !== portalEl) {
        portalEl.appendChild(pop);
      }
      portalActive = { add, pop, home, opts };
      // The shared anchoring engine pins the portal in page coordinates and
      // re-places on scroll and resize - the balloon itself stays
      // CSS-anchored, place() keeps owning the flip.
      if (portalSession) {
        portalSession.dispose();
      }
      portalSession = anchorAt(add, pop, {
        wrap: portalEl,
        position: false,
        onPlace: () => {
          if (
            portalActive &&
            portalActive.pop === pop &&
            pop.classList.contains('is-open')
          ) {
            place(add, pop, opts);
          }
        },
      });
      follow(add, pop, refresh);
    };

    const unportal = (pop, home) => {
      if (portalActive && portalActive.pop === pop) {
        portalActive = null;
      }
      if (!portalActive) {
        unfollow();
        if (portalSession) {
          portalSession.dispose();
          portalSession = null;
        }
      }
      if (home && home.isConnected) {
        home.appendChild(pop);
      } else {
        pop.remove();
      }
    };

    // Runs `reposition` whenever `trigger`'s box moves or resizes, until the
    // popover closes - the one way both portaled (reposition = refresh) and
    // in-flow (reposition = place) balloons stay stuck to a moving "+". The
    // rect guard makes each frame a cheap compare, and reposition never
    // moves the trigger, so there is no feedback loop. Only one popover is
    // open at a time, so a single watcher suffices.
    const follow = (trigger, pop, reposition) => {
      if (!window.requestAnimationFrame) {
        return;
      }
      if (followAdd === trigger && followRaf !== null) {
        return;
      }
      unfollow();
      followAdd = trigger;
      followRect = trigger.getBoundingClientRect();
      const tick = () => {
        if (!trigger.isConnected || !pop.classList.contains('is-open')) {
          unfollow();
          return;
        }
        const rect = trigger.getBoundingClientRect();
        if (
          rect.top !== followRect.top ||
          rect.left !== followRect.left ||
          rect.width !== followRect.width ||
          rect.height !== followRect.height
        ) {
          followRect = rect;
          reposition();
        }
        followRaf = window.requestAnimationFrame(tick);
      };
      followRaf = window.requestAnimationFrame(tick);
    };

    const unfollow = () => {
      if (followRaf !== null) {
        window.cancelAnimationFrame(followRaf);
      }
      followRaf = null;
      followAdd = null;
      followRect = null;
    };

    // Re-pins the open balloon to its trigger after anything moved it:
    // window resizes, or re-renders shifting the "+" as items land.
    const refresh = () => {
      if (
        portalActive &&
        portalActive.add.isConnected &&
        portalActive.pop.classList.contains('is-open')
      ) {
        portalFor(
          portalActive.add,
          portalActive.pop,
          portalActive.home,
          portalActive.opts,
        );
        place(portalActive.add, portalActive.pop, portalActive.opts);
      }
    };

    // Closes every open popover but keep: the portaled one synchronously
    // (its trigger is elsewhere in the page), the in-flow ones via their
    // aria-controls or sibling trigger.
    const closeOthers = (keep) => {
      berTags.tooltip.hide();
      if (portalActive && portalActive.pop !== keep) {
        const ended = portalActive;
        portalActive = null;
        ended.add.setAttribute('aria-expanded', 'false');
        ended.pop.classList.remove('is-open');
        unportal(ended.pop, ended.home);
      }
      document.querySelectorAll('.ber-tags__pop.is-open').forEach((pop) => {
        if (pop !== keep) {
          pop.classList.remove('is-open');
          const trigger =
            (pop.id && document.querySelector(`[aria-controls="${pop.id}"]`)) ||
            (pop.parentNode && pop.parentNode.querySelector('.ber-tags__add'));
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
          }
        }
      });
    };

    // The open/close skeleton of a portaled balloon: toggle on the trigger,
    // Escape and inner-click handling, aria state, portal in and out.
    const wire = ({ add, pop, home, opts = {}, onOpen, onClose }) => {
      const open = () => {
        closeOthers(pop);
        portalFor(add, pop, home, opts);
        pop.classList.add('is-open');
        add.setAttribute('aria-expanded', 'true');
        place(add, pop, opts);
        if (onOpen) {
          onOpen();
        }
      };
      const close = (refocus) => {
        pop.classList.remove('is-open');
        add.setAttribute('aria-expanded', 'false');
        if (onClose) {
          onClose();
        }
        unportal(pop, home);
        if (refocus && add.isConnected) {
          add.focus();
        }
      };
      add.addEventListener('click', () => {
        if (add.disabled) {
          return;
        }
        if (pop.classList.contains('is-open')) {
          close(false);
        } else {
          open();
        }
      });
      // Keep clicks inside the balloon from the global outside-closer.
      pop.addEventListener('click', (event) => event.stopPropagation());
      pop.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close(true);
        }
      });
      return { open, close };
    };

    // The one anchoring engine every popover runs on. Pins the portal
    // wrapper to the anchor in page coordinates - so scrolling moves both
    // natively, never a stale fixed position - and re-evaluates on scroll
    // and resize (scrolls inside the node, e.g. a balloon's own list, are
    // ignored). Two modes:
    //   position: true (default, the confirm) - centers the node over the
    //   anchor clamped to the viewport, flips below/above by the room
    //   rule, sets --ber-caret-x; onFlip(below) gets the side for the
    //   caller's caret classes.
    //   position: false (the portaled balloons) - the node is CSS-anchored
    //   and place() owns flipping; only the pin and the re-evaluation run.
    // onPlace() fires after every reposition, onDetach() when the anchor
    // leaves the DOM. Returns { place, dispose }; dispose detaches the
    // listeners (the caller removes the element itself).
    const anchorAt = (
      anchor,
      node,
      {
        wrap = null,
        gap = 8,
        margin = 8,
        caretInset = 12,
        position = true,
        onPlace = null,
        onFlip = null,
        onDetach = null,
      } = {},
    ) => {
      const reposition = () => {
        if (!anchor.isConnected) {
          dispose();
          if (onDetach) {
            onDetach();
          }
          return;
        }
        const rect = anchor.getBoundingClientRect();
        if (wrap) {
          wrap.style.left = `${rect.left + window.scrollX}px`;
          wrap.style.top = `${rect.top + window.scrollY}px`;
          wrap.style.width = `${rect.width}px`;
          wrap.style.height = `${rect.height}px`;
        }
        if (position) {
          const width = node.offsetWidth;
          const height = node.offsetHeight;
          // Clamped in viewport coordinates, then converted to wrapper-local.
          const left = Math.max(
            margin,
            Math.min(
              rect.left + rect.width / 2 - width / 2,
              document.documentElement.clientWidth - width - margin,
            ),
          );
          // gap only weighs the flip decision; the VISIBLE gap is CSS
          // (top/bottom: 100% + a scaled margin on the consumer), so it
          // follows --ber-scale with no scale math in JS.
          const belowSpace =
            document.documentElement.clientHeight - rect.bottom - gap;
          const aboveSpace = rect.top - gap;
          const below = !(height + gap > belowSpace && aboveSpace > belowSpace);
          node.style.left = `${left - rect.left}px`;
          const caret = Math.min(
            Math.max(rect.left + rect.width / 2 - left, caretInset),
            width - caretInset,
          );
          node.style.setProperty('--ber-caret-x', `${caret}px`);
          if (onFlip) {
            onFlip(below);
          }
        }
        if (onPlace) {
          onPlace();
        }
      };
      const onScroll = (event) => {
        if (event.target instanceof Node && node.contains(event.target)) {
          return;
        }
        reposition();
      };
      const dispose = () => {
        document.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', reposition);
      };
      document.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', reposition);
      reposition();
      return { place: reposition, dispose };
    };

    return {
      place,
      portalFor,
      unportal,
      refresh,
      follow,
      unfollow,
      closeOthers,
      wire,
      anchorAt,
    };
  })();

  berTags.popover = popover;

  // Document-level services: the outside-press closer for every popover and
  // the tooltip that must not float away from a scrolled target.
  Drupal.behaviors.berTagsApi = {
    attach() {
      once('ber-tags-doc', 'html').forEach((html) => {
        // pointerdown is immune to click retargeting on layout shifts.
        html.addEventListener('pointerdown', (event) => {
          const { target } = event;
          if (
            !(target instanceof Element) ||
            !target.closest('.ber-tags__anchor')
          ) {
            popover.closeOthers(null);
          }
        });
        // A scrolled-away tooltip would float detached from its target.
        document.addEventListener('scroll', () => berTags.tooltip.hide(), true);
      });
    },
  };
})(Drupal, once);
